/**
 * Shared batch-fetch hooks for order lists.
 *
 * Both the Orders page (OrderCardList) and the dashboard (RecentOrdersList)
 * render lists of Sales Orders that need, per visible order, (a) the line items
 * with thumbnails and (b) the customer avatar. Rather than each list issuing its
 * own N+1 requests, these hooks batch every visible order into a single query.
 *
 * Extracted from OrderCardList so both consumers share ONE implementation
 * (same query keys, response shape, and staleTime) — see approved proposal
 * t-e3d5ce.
 */

import { useQuery } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrderItemRow {
  parent: string;
  item_name: string;
  item_code: string;
  qty: number;
  rate: number;
  amount: number;
  image?: string;
}

// ---------------------------------------------------------------------------
// Hook: batch-fetch order line items for visible orders
// ---------------------------------------------------------------------------

export function useOrderItemsBatch(orderNames: string[]) {
  return useQuery({
    queryKey: ["order-items-batch", orderNames],
    queryFn: async () => {
      if (orderNames.length === 0) return [];
      const client = getERPClient();
      const rows = await client.list<Record<string, unknown>>({
        doctype: "Sales Order",
        filters: [["name", "in", orderNames]],
        fields: [
          "name",
          "`tabSales Order Item`.item_name as item_name",
          "`tabSales Order Item`.item_code as item_code",
          "`tabSales Order Item`.qty as qty",
          "`tabSales Order Item`.rate as rate",
          "`tabSales Order Item`.amount as amount",
          "`tabSales Order Item`.image as image",
        ],
        limit_page_length: 200,
        order_by: "`tabSales Order Item`.idx asc",
      });
      return rows.map((r) => ({
        parent: String(r.name ?? ""),
        item_name: String(r.item_name ?? ""),
        item_code: String(r.item_code ?? ""),
        qty: Number(r.qty ?? 0),
        rate: Number(r.rate ?? 0),
        amount: Number(r.amount ?? 0),
        image: r.image ? String(r.image) : undefined,
      }));
    },
    enabled: orderNames.length > 0,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

// ---------------------------------------------------------------------------
// Hook: batch-fetch customer avatars for visible orders
// ---------------------------------------------------------------------------

export function useCustomerAvatarsBatch(customerNames: string[]) {
  return useQuery({
    queryKey: ["customer-images-batch", customerNames],
    queryFn: async () => {
      if (customerNames.length === 0) return {};
      const client = getERPClient();
      // Server-side resolves the best avatar per customer: Customer.image if
      // set, else the LINE profile picture (Storefront Customer Profile
      // .line_picture_url), which login never copies onto Customer.image.
      const map = await client.call<Record<string, string>>(
        "zaviago_backend.api.customer_list.get_customer_avatars",
        { customer_names: customerNames },
      );
      return map ?? {};
    },
    enabled: customerNames.length > 0,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}
